import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building2, MapPin, Phone, Mail, User, Users, Briefcase,
  Plus, Pencil, Archive, RotateCcw, Star, Loader2,
  Clock, MessageSquare, Send, Landmark,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import API_BASE from '../../utils/apiBase';

const API = API_BASE;

const ORG_TYPE_LABELS = {
  care_home: 'Care Home',
  nursing_home: 'Nursing Home',
  supported_living: 'Supported Living',
  domiciliary: 'Domiciliary Care',
  hospital: 'Hospital',
  local_authority: 'Local Authority',
  nhs_trust: 'NHS Trust',
  agency: 'Agency',
  other: 'Other',
};

const STAGE_LABELS = {
  prospect: 'Prospect',
  active_client: 'Active client',
  inactive: 'Inactive',
};

const STAGE_COLORS = {
  prospect: 'bg-amber-50 text-amber-700 border border-amber-200',
  active_client: 'bg-green-50 text-green-700 border border-green-200',
  inactive: 'bg-gray-100 text-gray-600 border border-gray-200',
};

const CHANNEL_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'sms', label: 'SMS' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'other', label: 'Other' },
];
const CHANNEL_LABELS = Object.fromEntries(CHANNEL_OPTIONS.map((c) => [c.value, c.label]));

const LOCATION_TYPES = [
  { value: 'care_home', label: 'Care Home' },
  { value: 'supported_living', label: 'Supported Living' },
  { value: 'community', label: 'Community' },
  { value: 'other', label: 'Other' },
];
const LOCATION_TYPE_LABELS = Object.fromEntries(LOCATION_TYPES.map((l) => [l.value, l.label]));

const REQUEST_STATUS_COLORS = {
  open: 'bg-blue-50 text-blue-700 border border-blue-200',
  in_progress: 'bg-amber-50 text-amber-700 border border-amber-200',
  fulfilled: 'bg-green-50 text-green-700 border border-green-200',
  cancelled: 'bg-gray-100 text-gray-500 border border-gray-200',
};

const EMPTY_CONTACT = {
  name: '',
  job_title: '',
  email: '',
  phone: '',
  preferred_channel: 'email',
  care_location_ids: [],
  is_primary: false,
  is_staffing_contact: false,
  is_billing_contact: false,
  notes: '',
};

const EMPTY_LOCATION = {
  name: '',
  location_type: 'care_home',
  address_line_1: '',
  city: '',
  postcode: '',
  contact_name: '',
  contact_phone: '',
};

function authHeaders(json = false) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  if (json) headers['Content-Type'] = 'application/json';
  return headers;
}

export default function ClientDetailPage() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [org, setOrg] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [requests, setRequests] = useState([]);
  const [upcomingShifts, setUpcomingShifts] = useState(null); // null = unknown/unavailable
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  // Contact dialog
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState(EMPTY_CONTACT);
  const [savingContact, setSavingContact] = useState(false);
  const [invitingId, setInvitingId] = useState(null);
  const [busyContactId, setBusyContactId] = useState(null);

  // Location dialog
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationForm, setLocationForm] = useState(EMPTY_LOCATION);
  const [savingLocation, setSavingLocation] = useState(false);

  const locationById = React.useMemo(() => {
    const map = {};
    locations.forEach((l) => { map[l.id] = l; });
    return map;
  }, [locations]);

  const fetchOrg = useCallback(async () => {
    try {
      const res = await fetch(`${API}/organisations/${orgId}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setOrg(data.organisation || null);
      } else if (res.status === 404) {
        toast.error('Client organisation not found');
        setOrg(null);
      }
    } catch {
      toast.error('Failed to load client');
    }
  }, [orgId]);

  const fetchContacts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/organisations/${orgId}/contacts?include_inactive=true`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setContacts(data.contacts || []);
      }
    } catch { /* handled by page-level error */ }
  }, [orgId]);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await fetch(`${API}/organisations/${orgId}/care-locations`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setLocations(data.care_locations || []);
      }
    } catch { /* noop */ }
  }, [orgId]);

  const fetchRequests = useCallback(async () => {
    try {
      const res = await fetch(`${API}/organisations/${orgId}/staffing-requests`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setRequests(data.staffing_requests || []);
      }
    } catch { /* noop */ }
  }, [orgId]);

  const fetchUpcomingShifts = useCallback(async () => {
    // Best-effort: the shifts endpoint has no organisation filter, so we fetch upcoming
    // shifts and filter client-side by organisation_id. Hidden gracefully if unavailable.
    try {
      const nowIso = new Date().toISOString();
      const res = await fetch(`${API}/shifts?from_at=${encodeURIComponent(nowIso)}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        const all = data.shifts || [];
        const count = all.filter((s) => s.organisation_id === orgId).length;
        setUpcomingShifts(count);
      } else {
        setUpcomingShifts(null);
      }
    } catch {
      setUpcomingShifts(null);
    }
  }, [orgId]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchOrg(), fetchContacts(), fetchLocations(), fetchRequests()]);
    setLoading(false);
    fetchUpcomingShifts();
  }, [fetchOrg, fetchContacts, fetchLocations, fetchRequests, fetchUpcomingShifts]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ---------- Contact actions ----------
  const openCreateContact = () => {
    setEditingContact(null);
    setContactForm(EMPTY_CONTACT);
    setContactDialogOpen(true);
  };

  const openEditContact = (contact) => {
    setEditingContact(contact);
    setContactForm({
      name: contact.name || '',
      job_title: contact.job_title || '',
      email: contact.email || '',
      phone: contact.phone || '',
      preferred_channel: contact.preferred_channel || 'email',
      care_location_ids: contact.care_location_ids || [],
      is_primary: !!contact.is_primary,
      is_staffing_contact: !!contact.is_staffing_contact,
      is_billing_contact: !!contact.is_billing_contact,
      notes: contact.notes || '',
    });
    setContactDialogOpen(true);
  };

  const toggleFormLocation = (locId) => {
    setContactForm((prev) => {
      const has = prev.care_location_ids.includes(locId);
      return {
        ...prev,
        care_location_ids: has
          ? prev.care_location_ids.filter((id) => id !== locId)
          : [...prev.care_location_ids, locId],
      };
    });
  };

  const saveContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name.trim() || contactForm.name.trim().length < 2) {
      toast.error('Contact name is required');
      return;
    }
    setSavingContact(true);
    try {
      const payload = {
        name: contactForm.name.trim(),
        job_title: contactForm.job_title.trim() || null,
        email: contactForm.email.trim() || null,
        phone: contactForm.phone.trim() || null,
        preferred_channel: contactForm.preferred_channel,
        care_location_ids: contactForm.care_location_ids,
        is_primary: contactForm.is_primary,
        is_staffing_contact: contactForm.is_staffing_contact,
        is_billing_contact: contactForm.is_billing_contact,
        notes: contactForm.notes.trim() || null,
      };
      let res;
      if (editingContact) {
        res = await fetch(`${API}/organisations/${orgId}/contacts/${editingContact.id}`, {
          method: 'PATCH', headers: authHeaders(true), body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${API}/organisations/${orgId}/contacts`, {
          method: 'POST', headers: authHeaders(true), body: JSON.stringify(payload),
        });
      }
      if (res.ok) {
        toast.success(editingContact ? 'Contact updated' : 'Contact added');
        setContactDialogOpen(false);
        await Promise.all([fetchContacts(), fetchOrg()]);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save contact');
      }
    } catch {
      toast.error('Failed to save contact');
    } finally {
      setSavingContact(false);
    }
  };

  const patchContact = async (contact, body, successMsg) => {
    setBusyContactId(contact.id);
    try {
      const res = await fetch(`${API}/organisations/${orgId}/contacts/${contact.id}`, {
        method: 'PATCH', headers: authHeaders(true), body: JSON.stringify(body),
      });
      if (res.ok) {
        if (successMsg) toast.success(successMsg);
        await Promise.all([fetchContacts(), fetchOrg()]);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Update failed');
      }
    } catch {
      toast.error('Update failed');
    } finally {
      setBusyContactId(null);
    }
  };

  const setPrimary = (contact) => patchContact(contact, { is_primary: true }, `${contact.name} is now the primary contact`);
  const toggleStaffing = (contact) => patchContact(contact, { is_staffing_contact: !contact.is_staffing_contact }, contact.is_staffing_contact ? 'Removed staffing contact' : 'Set as staffing contact');
  const toggleBilling = (contact) => patchContact(contact, { is_billing_contact: !contact.is_billing_contact }, contact.is_billing_contact ? 'Removed billing contact' : 'Set as billing contact');

  const archiveContact = async (contact) => {
    setBusyContactId(contact.id);
    try {
      const res = await fetch(`${API}/organisations/${orgId}/contacts/${contact.id}/archive`, {
        method: 'POST', headers: authHeaders(),
      });
      if (res.ok) {
        toast.success(`${contact.name} deactivated`);
        await Promise.all([fetchContacts(), fetchOrg()]);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to deactivate');
      }
    } catch { toast.error('Failed to deactivate'); } finally { setBusyContactId(null); }
  };

  const restoreContact = async (contact) => {
    setBusyContactId(contact.id);
    try {
      const res = await fetch(`${API}/organisations/${orgId}/contacts/${contact.id}/restore`, {
        method: 'POST', headers: authHeaders(),
      });
      if (res.ok) {
        toast.success(`${contact.name} reactivated`);
        await Promise.all([fetchContacts(), fetchOrg()]);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to reactivate');
      }
    } catch { toast.error('Failed to reactivate'); } finally { setBusyContactId(null); }
  };

  const inviteContact = async (contact) => {
    if (!contact.email) {
      toast.error('Set an email on this contact first');
      return;
    }
    setInvitingId(contact.id);
    try {
      const res = await fetch(`${API}/organisations/${orgId}/contacts/${contact.id}/invite-client-hub`, {
        method: 'POST', headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || `Client Hub invite sent to ${contact.email}`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to send invite');
      }
    } catch { toast.error('Failed to send invite'); } finally { setInvitingId(null); }
  };

  // ---------- Location actions ----------
  const openCreateLocation = () => {
    setEditingLocation(null);
    setLocationForm(EMPTY_LOCATION);
    setLocationDialogOpen(true);
  };

  const openEditLocation = (loc) => {
    setEditingLocation(loc);
    setLocationForm({
      name: loc.name || '',
      location_type: loc.location_type || 'care_home',
      address_line_1: loc.address_line_1 || '',
      city: loc.city || '',
      postcode: loc.postcode || '',
      contact_name: loc.contact_name || '',
      contact_phone: loc.contact_phone || '',
    });
    setLocationDialogOpen(true);
  };

  const saveLocation = async (e) => {
    e.preventDefault();
    if (!locationForm.name.trim() || !locationForm.address_line_1.trim() || !locationForm.city.trim() || !locationForm.postcode.trim()) {
      toast.error('Name, address, city and postcode are required');
      return;
    }
    setSavingLocation(true);
    try {
      const payload = { ...locationForm, organisation_id: orgId };
      let res;
      if (editingLocation) {
        res = await fetch(`${API}/care-locations/${editingLocation.id}`, {
          method: 'PUT', headers: authHeaders(true), body: JSON.stringify(payload),
        });
      } else {
        res = await fetch(`${API}/care-locations`, {
          method: 'POST', headers: authHeaders(true), body: JSON.stringify(payload),
        });
      }
      if (res.ok) {
        toast.success(editingLocation ? 'Care location updated' : 'Care location added');
        setLocationDialogOpen(false);
        await fetchLocations();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save care location');
      }
    } catch {
      toast.error('Failed to save care location');
    } finally {
      setSavingLocation(false);
    }
  };

  const startStaffingRequest = () => navigate(`/portal/staffing-requests?org=${orgId}&create=1`);

  // ---------- Derived ----------
  const activeContacts = contacts.filter((c) => c.is_active !== false);
  const inactiveContacts = contacts.filter((c) => c.is_active === false);
  const primaryContact = activeContacts.find((c) => c.is_primary) || null;
  const openRequests = requests.filter((r) => r.status === 'open').length;

  const contactsForLocation = (locId) => activeContacts.filter((c) => (c.care_location_ids || []).includes(locId));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!org) {
    return (
      <div className="max-w-3xl mx-auto py-16 text-center">
        <p className="text-text-muted mb-4">This client record could not be found.</p>
        <Button variant="outline" onClick={() => navigate('/portal/organisations')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to clients
        </Button>
      </div>
    );
  }

  const stage = org.relationship_stage || 'active_client';
  const addressParts = [org.address_line_1, org.city, org.postcode].filter(Boolean);

  return (
    <div className="max-w-6xl mx-auto pb-16">
      {/* Back link */}
      <button
        type="button"
        onClick={() => navigate('/portal/organisations')}
        className="inline-flex items-center gap-1.5 text-sm text-text-muted hover:text-primary mb-4 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Clients
      </button>

      {/* ===== Organisation header ===== */}
      <div className="bg-white border border-gray-100 rounded-xl shadow-sm p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-semibold text-text-primary">{org.name}</h1>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STAGE_COLORS[stage] || STAGE_COLORS.prospect}`}>
                  {STAGE_LABELS[stage] || 'Prospect'}
                </span>
                {org.is_active === false && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">Archived</span>
                )}
              </div>
              <p className="text-sm text-text-muted mt-1">
                {ORG_TYPE_LABELS[org.organisation_type] || org.organisation_type || 'Client organisation'}
              </p>
              <div className="mt-3 space-y-1 text-sm">
                {primaryContact ? (
                  <p className="flex items-center gap-2 text-text-primary">
                    <User className="h-3.5 w-3.5 text-text-muted" />
                    {primaryContact.name}
                    {primaryContact.job_title && <span className="text-text-muted">· {primaryContact.job_title}</span>}
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">Primary</span>
                  </p>
                ) : org.primary_contact_name ? (
                  <p className="flex items-center gap-2 text-text-primary">
                    <User className="h-3.5 w-3.5 text-text-muted" />{org.primary_contact_name}
                  </p>
                ) : (
                  <p className="text-text-muted text-xs">No primary contact yet</p>
                )}
                {(primaryContact?.phone || org.primary_contact_phone) && (
                  <a href={`tel:${(primaryContact?.phone || org.primary_contact_phone).replace(/\s/g, '')}`} className="flex items-center gap-2 text-primary hover:underline">
                    <Phone className="h-3.5 w-3.5" />{primaryContact?.phone || org.primary_contact_phone}
                  </a>
                )}
                {(primaryContact?.email || org.primary_contact_email) && (
                  <a href={`mailto:${primaryContact?.email || org.primary_contact_email}`} className="flex items-center gap-2 text-primary hover:underline">
                    <Mail className="h-3.5 w-3.5" />{primaryContact?.email || org.primary_contact_email}
                  </a>
                )}
                {addressParts.length > 0 && (
                  <p className="flex items-center gap-2 text-text-muted">
                    <MapPin className="h-3.5 w-3.5" />{addressParts.join(', ')}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={openCreateContact}>
              <Plus className="h-4 w-4 mr-1.5" /> Add contact
            </Button>
            <Button size="sm" onClick={startStaffingRequest}>
              <Briefcase className="h-4 w-4 mr-1.5" /> New staffing request
            </Button>
          </div>
        </div>
      </div>

      {/* ===== Connected summary ===== */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <SummaryCard icon={Users} label="Contacts" value={activeContacts.length} onClick={() => setTab('contacts')} />
        <SummaryCard icon={MapPin} label="Care locations" value={locations.length} onClick={() => setTab('locations')} />
        <SummaryCard icon={Briefcase} label="Open staffing requests" value={openRequests} onClick={() => setTab('staffing')} />
        <SummaryCard icon={Clock} label="Upcoming shifts" value={upcomingShifts === null ? '—' : upcomingShifts} />
      </div>

      {/* ===== Tabs ===== */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="contacts">Contacts ({activeContacts.length})</TabsTrigger>
          <TabsTrigger value="locations">Locations ({locations.length})</TabsTrigger>
          <TabsTrigger value="staffing">Staffing ({requests.length})</TabsTrigger>
        </TabsList>

        {/* ---- Overview ---- */}
        <TabsContent value="overview">
          <div className="grid md:grid-cols-2 gap-4">
            <Panel title="Primary contact" action={<Button size="sm" variant="ghost" onClick={() => setTab('contacts')}>View all</Button>}>
              {primaryContact ? (
                <ContactSummaryRow contact={primaryContact} locationById={locationById} />
              ) : (
                <EmptyHint text="No primary contact set. Add a contact and mark it primary." />
              )}
            </Panel>
            <Panel title="Billing & staffing contacts">
              <div className="space-y-2">
                <RoleLine label="Billing" contact={activeContacts.find((c) => c.is_billing_contact)} />
                <RoleLine label="Staffing" contact={activeContacts.find((c) => c.is_staffing_contact)} />
              </div>
            </Panel>
            <Panel title="Care locations" action={<Button size="sm" variant="ghost" onClick={() => setTab('locations')}>Manage</Button>}>
              {locations.length ? (
                <ul className="space-y-1.5">
                  {locations.slice(0, 5).map((l) => (
                    <li key={l.id} className="flex items-center gap-2 text-sm text-text-primary">
                      <MapPin className="h-3.5 w-3.5 text-text-muted" />{l.name}
                      <span className="text-xs text-text-muted">· {[l.city, l.postcode].filter(Boolean).join(' ')}</span>
                    </li>
                  ))}
                </ul>
              ) : <EmptyHint text="No care locations linked yet." />}
            </Panel>
            <Panel title="Recent staffing requests" action={<Button size="sm" variant="ghost" onClick={() => setTab('staffing')}>View all</Button>}>
              {requests.length ? (
                <ul className="space-y-1.5">
                  {requests.slice(0, 5).map((r) => (
                    <li key={r.id} className="flex items-center justify-between text-sm">
                      <span className="text-text-primary">{r.role_required} × {r.quantity}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${REQUEST_STATUS_COLORS[r.status] || REQUEST_STATUS_COLORS.open}`}>{r.status}</span>
                    </li>
                  ))}
                </ul>
              ) : <EmptyHint text="No staffing requests yet." />}
            </Panel>
          </div>
        </TabsContent>

        {/* ---- Contacts ---- */}
        <TabsContent value="contacts">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-text-muted">{activeContacts.length} active contact{activeContacts.length !== 1 ? 's' : ''}</p>
            <Button size="sm" onClick={openCreateContact}><Plus className="h-4 w-4 mr-1.5" /> Add contact</Button>
          </div>
          {activeContacts.length === 0 && inactiveContacts.length === 0 ? (
            <EmptyState icon={Users} title="No contacts yet" text="Add the people you deal with at this client — primary, staffing and billing contacts." action={<Button size="sm" onClick={openCreateContact}><Plus className="h-4 w-4 mr-1.5" /> Add first contact</Button>} />
          ) : (
            <div className="space-y-3">
              {activeContacts.map((c) => (
                <ContactCard
                  key={c.id}
                  contact={c}
                  locationById={locationById}
                  busy={busyContactId === c.id}
                  inviting={invitingId === c.id}
                  onEdit={() => openEditContact(c)}
                  onSetPrimary={() => setPrimary(c)}
                  onToggleStaffing={() => toggleStaffing(c)}
                  onToggleBilling={() => toggleBilling(c)}
                  onArchive={() => archiveContact(c)}
                  onInvite={() => inviteContact(c)}
                />
              ))}
              {inactiveContacts.length > 0 && (
                <div className="pt-4">
                  <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">Inactive ({inactiveContacts.length})</p>
                  <div className="space-y-3">
                    {inactiveContacts.map((c) => (
                      <ContactCard
                        key={c.id}
                        contact={c}
                        locationById={locationById}
                        busy={busyContactId === c.id}
                        inactive
                        onRestore={() => restoreContact(c)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* ---- Locations ---- */}
        <TabsContent value="locations">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-text-muted">{locations.length} care location{locations.length !== 1 ? 's' : ''}</p>
            <Button size="sm" onClick={openCreateLocation}><Plus className="h-4 w-4 mr-1.5" /> Add location</Button>
          </div>
          {locations.length === 0 ? (
            <EmptyState icon={MapPin} title="No care locations" text="Add the sites where this client needs staff." action={<Button size="sm" onClick={openCreateLocation}><Plus className="h-4 w-4 mr-1.5" /> Add first location</Button>} />
          ) : (
            <div className="space-y-3">
              {locations.map((l) => {
                const linked = contactsForLocation(l.id);
                return (
                  <div key={l.id} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Landmark className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-text-primary text-sm flex items-center gap-2">
                            {l.name}
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{LOCATION_TYPE_LABELS[l.location_type] || l.location_type}</span>
                            {l.is_active === false && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">Archived</span>}
                          </p>
                          <p className="text-xs text-text-muted mt-0.5">{[l.address_line_1, l.city, l.postcode].filter(Boolean).join(', ')}</p>
                          {(l.contact_name || l.contact_phone) && (
                            <p className="text-xs text-text-muted mt-0.5">Site contact: {l.contact_name || '—'} {l.contact_phone ? `· ${l.contact_phone}` : ''}</p>
                          )}
                          <div className="mt-2">
                            <p className="text-[11px] text-text-muted mb-1">Linked contacts:</p>
                            {linked.length ? (
                              <div className="flex flex-wrap gap-1.5">
                                {linked.map((c) => (
                                  <span key={c.id} className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">{c.name}</span>
                                ))}
                              </div>
                            ) : <span className="text-[11px] text-text-muted italic">None yet</span>}
                          </div>
                        </div>
                      </div>
                      <Button size="sm" variant="outline" className="h-8" onClick={() => openEditLocation(l)}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ---- Staffing ---- */}
        <TabsContent value="staffing">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-text-muted">{openRequests} open · {requests.length} total</p>
            <Button size="sm" onClick={startStaffingRequest}><Plus className="h-4 w-4 mr-1.5" /> New staffing request</Button>
          </div>
          {requests.length === 0 ? (
            <EmptyState icon={Briefcase} title="No staffing requests" text="Raise a staffing request to start filling shifts for this client." action={<Button size="sm" onClick={startStaffingRequest}><Plus className="h-4 w-4 mr-1.5" /> New staffing request</Button>} />
          ) : (
            <div className="space-y-3">
              {requests.map((r) => (
                <div key={r.id} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-text-primary text-sm flex items-center gap-2">
                        {r.request_code && <span className="text-text-muted font-normal">{r.request_code}</span>}
                        {r.role_required} × {r.quantity}
                      </p>
                      <p className="text-xs text-text-muted mt-0.5">
                        {r.shift_pattern ? `${r.shift_pattern} · ` : ''}
                        {r.start_date ? `from ${r.start_date}` : ''}
                        {r.care_location_id && locationById[r.care_location_id] ? ` · ${locationById[r.care_location_id].name}` : ''}
                      </p>
                      {r.notes && <p className="text-xs text-text-muted mt-1 max-w-xl">{r.notes}</p>}
                    </div>
                    <div className="text-right">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${REQUEST_STATUS_COLORS[r.status] || REQUEST_STATUS_COLORS.open}`}>{r.status}</span>
                      {typeof r.shifts_created === 'number' && r.shifts_created > 0 && (
                        <p className="text-[10px] text-text-muted mt-1">{r.shifts_created} shift{r.shifts_created !== 1 ? 's' : ''} created</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ===== Contact dialog ===== */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingContact ? 'Edit contact' : 'Add contact'}</DialogTitle>
            <DialogDescription>People you deal with at {org.name}.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveContact} className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} placeholder="Full name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Job title</Label>
                <Input value={contactForm.job_title} onChange={(e) => setContactForm({ ...contactForm, job_title: e.target.value })} placeholder="e.g. Registered Manager" />
              </div>
              <div>
                <Label>Preferred contact</Label>
                <Select value={contactForm.preferred_channel} onValueChange={(v) => setContactForm({ ...contactForm, preferred_channel: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CHANNEL_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} placeholder="name@example.com" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} placeholder="Phone number" />
              </div>
            </div>

            <div className="space-y-2 rounded-lg border border-gray-100 p-3 bg-gray-50/50">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={contactForm.is_primary} onCheckedChange={(v) => setContactForm({ ...contactForm, is_primary: !!v })} />
                Primary contact
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={contactForm.is_staffing_contact} onCheckedChange={(v) => setContactForm({ ...contactForm, is_staffing_contact: !!v })} />
                Staffing contact
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={contactForm.is_billing_contact} onCheckedChange={(v) => setContactForm({ ...contactForm, is_billing_contact: !!v })} />
                Billing contact
              </label>
            </div>

            {locations.length > 0 && (
              <div>
                <Label>Linked care locations</Label>
                <div className="mt-1 space-y-1.5 max-h-40 overflow-y-auto rounded-lg border border-gray-100 p-2">
                  {locations.map((l) => (
                    <label key={l.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox checked={contactForm.care_location_ids.includes(l.id)} onCheckedChange={() => toggleFormLocation(l.id)} />
                      {l.name}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label>Notes</Label>
              <Textarea value={contactForm.notes} onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })} rows={2} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setContactDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={savingContact}>
                {savingContact ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingContact ? 'Save changes' : 'Add contact')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ===== Location dialog ===== */}
      <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLocation ? 'Edit care location' : 'Add care location'}</DialogTitle>
            <DialogDescription>Sites for {org.name}.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveLocation} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Name *</Label>
                <Input value={locationForm.name} onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })} />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={locationForm.location_type} onValueChange={(v) => setLocationForm({ ...locationForm, location_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LOCATION_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Address *</Label>
              <Input value={locationForm.address_line_1} onChange={(e) => setLocationForm({ ...locationForm, address_line_1: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>City *</Label>
                <Input value={locationForm.city} onChange={(e) => setLocationForm({ ...locationForm, city: e.target.value })} />
              </div>
              <div>
                <Label>Postcode *</Label>
                <Input value={locationForm.postcode} onChange={(e) => setLocationForm({ ...locationForm, postcode: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Site contact name</Label>
                <Input value={locationForm.contact_name} onChange={(e) => setLocationForm({ ...locationForm, contact_name: e.target.value })} />
              </div>
              <div>
                <Label>Site contact phone</Label>
                <Input value={locationForm.contact_phone} onChange={(e) => setLocationForm({ ...locationForm, contact_phone: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLocationDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={savingLocation}>
                {savingLocation ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingLocation ? 'Save changes' : 'Add location')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== Sub-components ====================

function SummaryCard({ icon: Icon, label, value, onClick }) {
  const clickable = typeof onClick === 'function';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={`text-left bg-white border border-gray-100 rounded-xl p-4 shadow-sm transition-colors ${clickable ? 'hover:border-primary/40 cursor-pointer' : 'cursor-default'}`}
    >
      <div className="flex items-center gap-2 text-text-muted mb-1">
        <Icon className="h-4 w-4" />
        <span className="text-xs">{label}</span>
      </div>
      <p className="text-2xl font-semibold text-text-primary">{value}</p>
    </button>
  );
}

function Panel({ title, action, children }) {
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function EmptyHint({ text }) {
  return <p className="text-xs text-text-muted italic">{text}</p>;
}

function EmptyState({ icon: Icon, title, text, action }) {
  return (
    <div className="bg-white border border-dashed border-gray-200 rounded-xl p-10 text-center">
      <div className="h-12 w-12 rounded-xl bg-gray-50 flex items-center justify-center mx-auto mb-3">
        <Icon className="h-6 w-6 text-text-muted" />
      </div>
      <p className="font-medium text-text-primary">{title}</p>
      <p className="text-sm text-text-muted mt-1 mb-4 max-w-sm mx-auto">{text}</p>
      {action}
    </div>
  );
}

function RoleLine({ label, contact }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-text-muted">{label}</span>
      {contact ? (
        <span className="text-text-primary">{contact.name}</span>
      ) : (
        <span className="text-xs text-text-muted italic">Not set</span>
      )}
    </div>
  );
}

function ContactBadges({ contact }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {contact.is_primary && <Badge className="bg-primary/10 text-primary border-0 hover:bg-primary/10"><Star className="h-3 w-3 mr-1" />Primary</Badge>}
      {contact.is_staffing_contact && <Badge className="bg-blue-50 text-blue-700 border-0 hover:bg-blue-50">Staffing</Badge>}
      {contact.is_billing_contact && <Badge className="bg-purple-50 text-purple-700 border-0 hover:bg-purple-50">Billing</Badge>}
    </div>
  );
}

function ContactSummaryRow({ contact, locationById }) {
  const locs = (contact.care_location_ids || []).map((id) => locationById[id]?.name).filter(Boolean);
  return (
    <div className="space-y-1">
      <p className="font-medium text-text-primary text-sm flex items-center gap-2">{contact.name} <ContactBadges contact={contact} /></p>
      {contact.job_title && <p className="text-xs text-text-muted">{contact.job_title}</p>}
      {contact.email && <p className="text-xs text-primary">{contact.email}</p>}
      {contact.phone && <p className="text-xs text-text-muted">{contact.phone}</p>}
      {locs.length > 0 && <p className="text-xs text-text-muted">Sites: {locs.join(', ')}</p>}
    </div>
  );
}

function ContactCard({
  contact, locationById, busy, inviting, inactive,
  onEdit, onSetPrimary, onToggleStaffing, onToggleBilling, onArchive, onInvite, onRestore,
}) {
  const locs = (contact.care_location_ids || []).map((id) => locationById[id]?.name).filter(Boolean);
  return (
    <div className={`bg-white border rounded-xl p-4 shadow-sm ${inactive ? 'border-gray-100 opacity-70' : 'border-gray-100'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-9 w-9 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
            <User className="h-4 w-4 text-text-muted" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-medium text-text-primary text-sm">{contact.name}</p>
              <ContactBadges contact={contact} />
              {inactive && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">Inactive</span>}
            </div>
            {contact.job_title && <p className="text-xs text-text-muted mt-0.5">{contact.job_title}</p>}
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-xs">
              {contact.email && <a href={`mailto:${contact.email}`} className="text-primary hover:underline flex items-center gap-1"><Mail className="h-3 w-3" />{contact.email}</a>}
              {contact.phone && <a href={`tel:${contact.phone.replace(/\s/g, '')}`} className="text-text-muted hover:underline flex items-center gap-1"><Phone className="h-3 w-3" />{contact.phone}</a>}
              {contact.preferred_channel && <span className="text-text-muted flex items-center gap-1"><MessageSquare className="h-3 w-3" />Prefers {CHANNEL_LABELS[contact.preferred_channel] || contact.preferred_channel}</span>}
            </div>
            {locs.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {locs.map((n) => <span key={n} className="text-[11px] px-2 py-0.5 rounded-full bg-gray-50 text-text-muted border border-gray-100 flex items-center gap-1"><MapPin className="h-3 w-3" />{n}</span>)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-50">
        {inactive ? (
          <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={onRestore}>
            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reactivate
          </Button>
        ) : (
          <>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onEdit}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
            {!contact.is_primary && (
              <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={onSetPrimary}><Star className="h-3.5 w-3.5 mr-1" />Set primary</Button>
            )}
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={onToggleStaffing}>
              {contact.is_staffing_contact ? 'Unset staffing' : 'Set staffing'}
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={onToggleBilling}>
              {contact.is_billing_contact ? 'Unset billing' : 'Set billing'}
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={inviting || !contact.email} onClick={onInvite} title={!contact.email ? 'Add an email first' : 'Send Client Hub invite'}>
              {inviting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
              {inviting ? 'Sending…' : 'Invite to Client Hub'}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-amber-600 hover:text-amber-700 ml-auto" disabled={busy} onClick={onArchive}>
              <Archive className="h-3.5 w-3.5 mr-1" /> Deactivate
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
